import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button, Icon, Loader, Message } from 'semantic-ui-react';
import { Document, Page, pdfjs } from 'react-pdf';
import { buildFileUrl, getDisplayFileName } from '../../utils/fileUtils';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './PdfPreviewModal.scss';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const MIN_SCALE = 0.5;
const MAX_SCALE = 2;
const SCALE_STEP = 0.25;

export const PdfPreviewModal = ({ open, onClose, filePath }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fileUrl = buildFileUrl(filePath);
  const displayName = getDisplayFileName(filePath);

  useEffect(() => {
    if (open) {
      setNumPages(null);
      setPageNumber(1);
      setScale(1);
      setLoading(true);
      setError(null);
    }
  }, [open, filePath]);

  const handleDocumentLoadSuccess = useCallback(({ numPages: totalPages }) => {
    setNumPages(totalPages);
    setPageNumber(1);
    setLoading(false);
    setError(null);
  }, []);

  const handleDocumentLoadError = useCallback(() => {
    setLoading(false);
    setError('No se pudo previsualizar el archivo PDF.');
  }, []);

  const goToPreviousPage = () => {
    setPageNumber((current) => Math.max(current - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((current) => Math.min(current + 1, numPages || current));
  };

  const zoomOut = () => {
    setScale((current) => Math.max(current - SCALE_STEP, MIN_SCALE));
  };

  const zoomIn = () => {
    setScale((current) => Math.min(current + SCALE_STEP, MAX_SCALE));
  };

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>{displayName || 'Previsualización PDF'}</Modal.Header>
      <Modal.Content scrolling>
        <div className="pdf-preview-modal">
          <div className="pdf-preview-modal__toolbar">
            <div className="pdf-preview-modal__toolbar-group">
              <Button
                icon
                labelPosition="left"
                size="small"
                onClick={goToPreviousPage}
                disabled={pageNumber <= 1 || !!error}
              >
                <Icon name="angle left" />
                Anterior
              </Button>
              <span className="pdf-preview-modal__page-info">
                {numPages ? `Página ${pageNumber} de ${numPages}` : 'Cargando...'}
              </span>
              <Button
                icon
                labelPosition="right"
                size="small"
                onClick={goToNextPage}
                disabled={!numPages || pageNumber >= numPages || !!error}
              >
                Siguiente
                <Icon name="angle right" />
              </Button>
            </div>
            <div className="pdf-preview-modal__toolbar-group">
              <Button icon size="small" onClick={zoomOut} disabled={scale <= MIN_SCALE || !!error}>
                <Icon name="search minus" />
              </Button>
              <span>{Math.round(scale * 100)}%</span>
              <Button icon size="small" onClick={zoomIn} disabled={scale >= MAX_SCALE || !!error}>
                <Icon name="search plus" />
              </Button>
              <Button
                as="a"
                href={fileUrl}
                download
                size="small"
                icon
                labelPosition="left"
              >
                <Icon name="download" />
                Descargar
              </Button>
              <Button
                as="a"
                href={fileUrl}
                target="_blank"
                rel="noreferrer"
                size="small"
                icon
                labelPosition="left"
              >
                <Icon name="external alternate" />
                Abrir pestaña
              </Button>
            </div>
          </div>

          {error ? (
            <div className="pdf-preview-modal__error">
              <Message negative>{error}</Message>
              <Button
                as="a"
                href={fileUrl}
                target="_blank"
                rel="noreferrer"
                primary
              >
                Abrir en pestaña nueva
              </Button>
            </div>
          ) : (
            <div className="pdf-preview-modal__viewer">
              {loading && (
                <div className="pdf-preview-modal__loading">
                  <Loader active inline="centered" />
                </div>
              )}
              <Document
                file={fileUrl}
                onLoadSuccess={handleDocumentLoadSuccess}
                onLoadError={handleDocumentLoadError}
                loading=""
              >
                {!loading && numPages && (
                  <Page
                    pageNumber={pageNumber}
                    scale={scale}
                    className="pdf-preview-modal__page"
                    renderTextLayer
                    renderAnnotationLayer
                  />
                )}
              </Document>
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cerrar</Button>
      </Modal.Actions>
    </Modal>
  );
};
